import React, { useState } from 'react';
import { Modal, Form, Input, Select, Switch, Button, Alert, Spin } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { withUserType } from '../withUserType';
import useMountEffect from '../../hooks/useMountEffect';
import OWButton from '../ow_button/OWButton';
import {
	ENTITY_REQUEST_ANSWER_TYPES,
	ENTITY_REQUEST_ANSWER_TYPE_DISPLAY_NAMES,
	ENTITY_REQUEST_ENTITY_TYPES,
} from '../../types/entity_add_requests';
import {
	entityRequestFormsRestCrudThunks,
	fetchActiveEntityRequestForm,
} from '../../thunks/entity_request_forms_thunks';

const FORM_ID = 'entityRequestFormBuilder';
const ADD_QUESTION_COLOR = '#6027b0';

const ANSWER_TYPE_OPTIONS = Object.keys(ENTITY_REQUEST_ANSWER_TYPE_DISPLAY_NAMES).map((value) => ({
	value,
	label: ENTITY_REQUEST_ANSWER_TYPE_DISPLAY_NAMES[value],
}));

const EMPTY_QUESTION = {
	questionText: '',
	answerType: ENTITY_REQUEST_ANSWER_TYPES.short_text,
	required: false,
	optionsText: '',
};

// Multiple-choice options are entered as one comma-separated string; parse to a trimmed, non-empty
// list. Used both by the save serializer and the field validator so they agree on what "empty" means.
const parseOptionsText = (optionsText) =>
	String(optionsText || '')
		.split(',')
		.map((option) => option.trim())
		.filter((option) => !!option);

// Small uppercase field caption ("QUESTION", "ANSWER TYPE", "OPTIONS (COMMA SEPARATED)").
const FieldLabel = ({ children }) => (
	<div className="footnoteMedium uppercase tracking-wide text-character-secondary">{children}</div>
);

// One editable question block within the form builder's Form.List.
const QuestionRow = ({ name, restField, remove }) => {
	const handleRemove = () => remove(name);
	const renderOptionsField = ({ getFieldValue }) =>
		getFieldValue(['questions', name, 'answerType']) ===
		ENTITY_REQUEST_ANSWER_TYPES.multiple_choice ? (
			<div className="mt-3">
				<FieldLabel>Options (comma separated)</FieldLabel>
				<Form.Item
					{...restField}
					className="mb-0"
					name={[name, 'optionsText']}
					rules={[
						{ required: true, message: 'Add at least one option.' },
						{
							validator: (_rule, value) =>
								parseOptionsText(value).length > 0
									? Promise.resolve()
									: Promise.reject(new Error('Add at least one option.')),
						},
					]}
				>
					<Input placeholder="Option one, Option two, Option three" />
				</Form.Item>
			</div>
		) : null;

	return (
		<div className="rounded-lg border border-solid border-grey-4 bg-grey-2 p-4">
			<FieldLabel>Question</FieldLabel>
			<div className="flex flex-row items-start" style={{ gap: '8px' }}>
				<Form.Item
					{...restField}
					className="mb-3 flex-1"
					name={[name, 'questionText']}
					rules={[{ required: true, message: 'Question text is required.' }]}
				>
					<Input placeholder="e.g. Why hasn't this supplier been added yet?" />
				</Form.Item>
				<Button
					danger
					icon={<DeleteOutlined />}
					onClick={handleRemove}
					style={{ marginTop: '4px' }}
				/>
			</div>
			<FieldLabel>Answer Type</FieldLabel>
			<div className="flex flex-row items-center" style={{ gap: '12px' }}>
				<Form.Item
					{...restField}
					className="mb-0"
					name={[name, 'answerType']}
					rules={[{ required: true, message: 'Answer type is required.' }]}
				>
					<Select style={{ width: 200 }} options={ANSWER_TYPE_OPTIONS} />
				</Form.Item>
				<label className="mb-0 flex items-center gap-2 pb-6">
					<Form.Item {...restField} noStyle name={[name, 'required']} valuePropName="checked">
						<Switch />
					</Form.Item>
					<span className="bodyregular text-character-primary" style={{ lineHeight: '22px' }}>
						Required
					</span>
				</label>
			</div>
			<Form.Item noStyle shouldUpdate>
				{renderOptionsField}
			</Form.Item>
		</div>
	);
};

function EntityRequestFormBuilder({
	visible,
	hideModal,
	onSuccess,
	fetchActiveForm,
	createForm,
	updateForm,
}) {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(true);
	const [existingFormId, setExistingFormId] = useState(undefined);
	const [initialQuestions, setInitialQuestions] = useState([EMPTY_QUESTION]);
	const [saving, setSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState(undefined);
	// A real load failure (network / permission) for the active form — distinct from the resolved-null
	// "no active form" case. Blocks saving so a transient error can't replace the company-wide form.
	const [formLoadFailed, setFormLoadFailed] = useState(false);

	useMountEffect(() => {
		fetchActiveForm(ENTITY_REQUEST_ENTITY_TYPES.supplier)
			.then((activeForm) => {
				if (activeForm) {
					setExistingFormId(nullSafeGet('id', activeForm));
					const questions = nullSafeGet('questions', activeForm) || [];
					const mapped = questions.map((question) => ({
						...question,
						optionsText: (question.options || []).join(', '),
					}));
					setInitialQuestions(mapped.length > 0 ? mapped : [EMPTY_QUESTION]);
				}
			})
			.catch(() => {
				// Real load error (not the resolved-null "no active form" case) — block saving so a
				// transient failure can't create/replace the company-wide request form.
				setFormLoadFailed(true);
			})
			.finally(() => {
				setLoading(false);
			});
	});

	const handleSubmit = (values) => {
		setErrorMessage(undefined);
		setSaving(true);
		const questions = (values.questions || []).map((question, index) => ({
			questionText: question.questionText,
			answerType: question.answerType,
			required: !!question.required,
			options:
				question.answerType === ENTITY_REQUEST_ANSWER_TYPES.multiple_choice
					? String(question.optionsText || '')
							.split(',')
							.map((option) => option.trim())
							.filter((option) => !!option)
					: [],
			orderingId: index + 1,
		}));
		const entity = {
			entityType: ENTITY_REQUEST_ENTITY_TYPES.supplier,
			isActive: true,
			questions,
		};
		const persist = existingFormId
			? updateForm({ ...entity, id: existingFormId })
			: createForm(entity);
		persist
			.then(() => {
				setSaving(false);
				onSuccess();
				hideModal();
			})
			.catch((error) => {
				setSaving(false);
				setErrorMessage(
					typeof error === 'string' ? error : 'Something went wrong saving the form.'
				);
			});
	};

	const renderQuestionList = (fields, { add, remove }) => {
		const handleAddQuestion = () => add({ ...EMPTY_QUESTION });
		return (
			<div className="flex flex-col" style={{ gap: '16px' }}>
				{fields.map(({ key, name, ...restField }) => (
					<QuestionRow key={key} name={name} restField={restField} remove={remove} />
				))}
				<Button
					type="dashed"
					block
					icon={<PlusOutlined />}
					onClick={handleAddQuestion}
					className="uppercase"
					style={{ color: ADD_QUESTION_COLOR, height: 44 }}
				>
					Add Question
				</Button>
			</div>
		);
	};

	const modalTitle = (
		<div>
			<div className="h5medium text-character-primary">Request to add a Supplier</div>
			<div className="footnoteregular mt-1 text-character-secondary">
				Questions to show requesters filling out the supplier form
			</div>
		</div>
	);

	return (
		<Modal
			visible={visible}
			width={700}
			title={modalTitle}
			onCancel={hideModal}
			closable={true}
			destroyOnClose={true}
			footer={[
				<OWButton key="cancel" type="default" size="large" onClick={hideModal}>
					Cancel
				</OWButton>,
				<OWButton
					key="save"
					type="primary"
					size="large"
					htmlType="submit"
					form={FORM_ID}
					loading={saving}
					disabled={formLoadFailed}
				>
					Save Form
				</OWButton>,
			]}
		>
			{loading ? (
				<div className="flex w-full justify-center py-8">
					<Spin />
				</div>
			) : formLoadFailed ? (
				<Alert
					type="error"
					showIcon
					message="Couldn't load the request form"
					description="Please close and try again — the form can't be edited or saved until it loads."
				/>
			) : (
				<Form
					id={FORM_ID}
					form={form}
					layout="vertical"
					preserve={false}
					initialValues={{ questions: initialQuestions }}
					onFinish={handleSubmit}
				>
					{errorMessage ? <Alert message={errorMessage} type="error" className="mb-4" /> : null}
					<Form.List name="questions">{renderQuestionList}</Form.List>
				</Form>
			)}
		</Modal>
	);
}

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchActiveForm: (entityType) =>
		dispatch(fetchActiveEntityRequestForm(ownProps.userType)(entityType)),
	createForm: (entity) =>
		dispatch(entityRequestFormsRestCrudThunks(ownProps.userType).create(entity)),
	updateForm: (entity) =>
		dispatch(entityRequestFormsRestCrudThunks(ownProps.userType).update(entity)),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(EntityRequestFormBuilder));
